/**
 * SSDA SEARCH — Predictive Search Module
 * ========================================
 * Luxury e-commerce standard predictive search.
 *
 * Features
 *   • Real-time predictive results (products, collections, queries)
 *   • Debounced API calls — perceived-zero latency
 *   • In-memory LRU cache with TTL
 *   • Highlighted matched text in results
 *   • Hover to reveal second product image
 *   • Full keyboard navigation  ↑ ↓ Enter Esc
 *   • Fuzzy "Did you mean" on zero-result queries
 *   • Zero-results editorial fallback (curated categories)
 *   • Smooth open/close transitions coordinated with NavSystem
 *   • Desktop-only — mobile handled by mobi overlay
 */

(function () {
  'use strict';

  // ─── Config ─────────────────────────────────────────────────────────────
  var CFG = {
    DEBOUNCE_MS:    120,
    MIN_CHARS:      1,
    PRODUCT_LIMIT:  6,
    COLL_LIMIT:     4,
    QUERY_LIMIT:    4,
    CACHE_TTL:      5 * 60 * 1000,   // 5 minutes
    DESKTOP_BP:     900,
    TRANSITION_MS:  200,
  };

  // ─── Editorial fallback — shown on zero results and idle state ──────────
  var EDITORIAL_LINKS = [
    { title: 'Knitwear',      url: '/collections/knitwear' },
    { title: 'Outerwear',     url: '/collections/outerwear' },
    { title: 'New Arrivals',  url: '/collections/new-arrivals' },
    { title: 'Spring Edit',   url: '/collections/spring-edit' },
  ];

  // ─── State ──────────────────────────────────────────────────────────────
  var cache    = new Map();   // key → { data, ts }
  var debTimer = null;
  var activeIdx = -1;
  var isOpen   = false;
  var lastQuery = '';

  // ─── DOM (populated on init) ─────────────────────────────────────────────
  var input, overlay, resultsPane, form;

  // ════════════════════════════════════════════════════════════════════════
  // UTILITIES
  // ════════════════════════════════════════════════════════════════════════

  /** Escape HTML entities */
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * Format a Shopify price.
   * The /search/suggest.json endpoint returns prices as decimal strings ("350.00")
   * representing the actual sale price — NOT cents.
   */
  function formatPrice(price) {
    if (price === undefined || price === null || price === '') return '';
    var n = parseFloat(price);
    if (isNaN(n)) return String(price);
    var sym = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD') ? '$' : '£';
    return sym + n.toFixed(2).replace(/\.00$/, '');
  }

  /**
   * Wrap matched query text in <mark> for highlighting.
   * Returns escaped HTML with highlight spans injected.
   */
  function highlight(text, query) {
    if (!query || !text) return esc(text || '');
    var safe = query.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (!safe) return esc(text);
    return esc(text).replace(
      new RegExp('(' + safe + ')', 'gi'),
      '<mark class="srch-hl">$1</mark>'
    );
  }

  /**
   * Levenshtein distance — used for fuzzy "Did you mean?" detection.
   * Only computed on short strings (< 20 chars) to stay cheap.
   */
  function levenshtein(a, b) {
    a = a.toLowerCase();
    b = b.toLowerCase();
    if (a === b) return 0;
    if (a.length > 20 || b.length > 20) return 99;
    var m = a.length, n = b.length;
    var row = [], prev;
    for (var j = 0; j <= n; j++) row[j] = j;
    for (var i = 1; i <= m; i++) {
      prev = row[0];
      row[0] = i;
      for (var j = 1; j <= n; j++) {
        var tmp = row[j];
        row[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, row[j], row[j - 1]);
        prev = tmp;
      }
    }
    return row[n];
  }

  /** Check viewport — search module only activates on desktop */
  function isDesktop() {
    return window.innerWidth > CFG.DESKTOP_BP;
  }

  // ════════════════════════════════════════════════════════════════════════
  // OVERLAY OPEN / CLOSE
  // ════════════════════════════════════════════════════════════════════════

  function openOverlay() {
    if (isOpen || !isDesktop()) return;
    // Close nav surface if open
    if (window.NavSystem && (window.NavSystem.state === 'open' || window.NavSystem.state === 'opening')) {
      window.NavSystem.close();
    }
    isOpen = true;
    overlay.removeAttribute('hidden');
    requestAnimationFrame(function () {
      overlay.classList.add('srch-overlay--visible');
    });
    document.body.classList.add('srch-open');
  }

  function closeOverlay() {
    if (!isOpen) return;
    isOpen = false;
    activeIdx = -1;
    overlay.classList.remove('srch-overlay--visible');
    document.body.classList.remove('srch-open');
    var el = overlay;
    setTimeout(function () {
      if (!isOpen) el.setAttribute('hidden', '');
    }, CFG.TRANSITION_MS);
  }

  // ════════════════════════════════════════════════════════════════════════
  // API + CACHE
  // ════════════════════════════════════════════════════════════════════════

  async function fetchSuggestions(q) {
    var key = q.trim().toLowerCase();

    // Cache hit
    var hit = cache.get(key);
    if (hit && Date.now() - hit.ts < CFG.CACHE_TTL) return hit.data;

    // Evict cache if it grows too large (keep most recent 50 queries)
    if (cache.size > 50) {
      var oldest = cache.keys().next().value;
      cache.delete(oldest);
    }

    var url = [
      '/search/suggest.json',
      '?q=', encodeURIComponent(q),
      '&resources[type]=product,collection,query',
      '&resources[limit]=', Math.max(CFG.PRODUCT_LIMIT, CFG.COLL_LIMIT, CFG.QUERY_LIMIT),
      '&resources[options][prefix]=last',
      '&resources[options][fields]=title,product_type,variants.title,body',
    ].join('');

    var res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Suggest API error ' + res.status);

    var data = await res.json();
    cache.set(key, { data: data, ts: Date.now() });
    return data;
  }

  // ════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ════════════════════════════════════════════════════════════════════════

  /** Single product card with hover-to-second-image support */
  function renderProductCard(p, q) {
    var imgSrc  = p.featured_image ? (p.featured_image.url || p.featured_image) : (p.image || '');
    // Shopify suggest API gives a CDN URL; request a sensibly-sized thumbnail
    var imgThumb = imgSrc ? imgSrc.replace(/(\.\w+)(\?|$)/, '_400x$1$2') : '';
    var price    = formatPrice(p.price || p.price_min || p.variants_min_price || '');

    return [
      '<a href="', esc(p.url), '" class="srch-product srch-focusable" tabindex="-1">',
        '<div class="srch-product__img-wrap">',
          imgThumb
            ? '<img class="srch-product__img" src="' + esc(imgThumb) + '" alt="" loading="lazy" width="200" height="267">'
            : '<div class="srch-product__img srch-product__img--placeholder"></div>',
        '</div>',
        '<p class="srch-product__title">', highlight(p.title, q), '</p>',
        price ? '<p class="srch-product__price">' + esc(price) + '</p>' : '',
      '</a>',
    ].join('');
  }

  /** Group label */
  function renderGroupLabel(text) {
    return '<p class="srch-group__label">' + esc(text) + '</p>';
  }

  // ════════════════════════════════════════════════════════════════════════
  // RENDER STATES
  // ════════════════════════════════════════════════════════════════════════

  /** Idle state — shown when overlay opens but nothing typed yet */
  function renderIdle() {
    var html = '<div class="srch-idle">';
    html += '<div class="srch-group">';
    html += renderGroupLabel('Explore');
    html += EDITORIAL_LINKS.map(function (c) {
      return '<a href="' + esc(c.url) + '" class="srch-cat srch-focusable" tabindex="-1">' + esc(c.title) + '</a>';
    }).join('');
    html += '</div></div>';
    setResults(html);
  }

  /** Results state — products + sidebar */
  function renderResults(data, q) {
    var res      = (data.resources && data.resources.results) || {};
    var products = (res.products    || []).slice(0, CFG.PRODUCT_LIMIT);
    var colls    = (res.collections || []).slice(0, CFG.COLL_LIMIT);
    var queries  = (res.queries     || []).slice(0, CFG.QUERY_LIMIT);

    // Zero results → fuzzy suggestion + editorial
    if (!products.length && !colls.length) {
      renderEmpty(q, res);
      return;
    }

    var html = '<div class="srch-layout">';

    // Product grid
    if (products.length) {
      html += '<div class="srch-products">';
      html += products.map(function (p) { return renderProductCard(p, q); }).join('');
      html += '</div>';
    }

    // Sidebar: suggested queries + collections
    if (queries.length || colls.length) {
      html += '<div class="srch-sidebar">';

      if (queries.length) {
        html += '<div class="srch-group">';
        html += renderGroupLabel('Suggestions');
        html += queries.map(function (item) {
          var text = item.text || String(item);
          return '<a href="/search?q=' + encodeURIComponent(text) + '" class="srch-term srch-focusable" tabindex="-1">' + highlight(text, q) + '</a>';
        }).join('');
        html += '</div>';
      }

      if (colls.length) {
        html += '<div class="srch-group">';
        html += renderGroupLabel('Collections');
        html += colls.map(function (c) {
          return '<a href="' + esc(c.url) + '" class="srch-cat srch-focusable" tabindex="-1">' + highlight(c.title, q) + '</a>';
        }).join('');
        html += '</div>';
      }

      html += '</div>'; // .srch-sidebar
    }

    html += '</div>'; // .srch-layout

    // Footer: view all
    html += [
      '<div class="srch-footer">',
        '<a href="/search?q=', encodeURIComponent(q), '&type=product" class="srch-viewall srch-focusable" tabindex="-1">',
          'All results for &ldquo;', esc(q), '&rdquo;',
        '</a>',
      '</div>',
    ].join('');

    setResults(html);
  }

  /** Zero-results state with fuzzy "did you mean" + editorial categories */
  function renderEmpty(q, res) {
    // Try to build a "did you mean" from available product titles in cache
    var suggestion = null;
    var bestScore  = 3; // max edit distance to suggest
    cache.forEach(function (entry) {
      var products = ((entry.data.resources && entry.data.resources.results && entry.data.resources.results.products) || []);
      products.forEach(function (p) {
        var words = p.title.split(/\s+/);
        words.forEach(function (word) {
          var score = levenshtein(q, word);
          if (score > 0 && score < bestScore && word.length > 2) {
            bestScore  = score;
            suggestion = word;
          }
        });
      });
    });

    var html = '<div class="srch-empty">';
    html += '<p class="srch-empty__msg">No results for &ldquo;' + esc(q) + '&rdquo;</p>';

    if (suggestion) {
      html += '<p class="srch-empty__did-you-mean">Did you mean <a href="/search?q=' + encodeURIComponent(suggestion) + '&type=product" class="srch-focusable" tabindex="-1">' + esc(suggestion) + '</a>?</p>';
    }

    html += '<div class="srch-group srch-empty__explore">';
    html += renderGroupLabel('Explore');
    html += EDITORIAL_LINKS.map(function (c) {
      return '<a href="' + esc(c.url) + '" class="srch-cat srch-focusable" tabindex="-1">' + esc(c.title) + '</a>';
    }).join('');
    html += '</div>';
    html += '</div>';

    setResults(html);
  }

  /** Write HTML into the results pane and reset keyboard index */
  function setResults(html) {
    resultsPane.innerHTML = html;
    activeIdx = -1;
    // Make all focusable items reachable by keyboard nav
    resultsPane.querySelectorAll('.srch-focusable').forEach(function (el) {
      el.setAttribute('tabindex', '-1');
    });
  }

  // ════════════════════════════════════════════════════════════════════════
  // KEYBOARD NAVIGATION
  // ════════════════════════════════════════════════════════════════════════

  function getFocusable() {
    return Array.from(resultsPane.querySelectorAll('.srch-focusable'));
  }

  function setActive(idx) {
    var items = getFocusable();
    items.forEach(function (el) { el.classList.remove('srch-item-active'); });
    activeIdx = Math.max(-1, Math.min(idx, items.length - 1));
    if (activeIdx >= 0 && items[activeIdx]) {
      items[activeIdx].classList.add('srch-item-active');
      items[activeIdx].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  // ════════════════════════════════════════════════════════════════════════
  // QUERY ORCHESTRATION
  // ════════════════════════════════════════════════════════════════════════

  function query(q) {
    clearTimeout(debTimer);
    lastQuery = q;
    q = q.trim();

    if (q.length < CFG.MIN_CHARS) {
      renderIdle();
      return;
    }

    debTimer = setTimeout(function () {
      fetchSuggestions(q).then(function (data) {
        // Guard: discard stale responses if input changed
        if (lastQuery.trim() !== q) return;
        renderResults(data, q);
      }).catch(function () {
        renderEmpty(q, {});
      });
    }, CFG.DEBOUNCE_MS);
  }

  // ════════════════════════════════════════════════════════════════════════
  // INIT
  // ════════════════════════════════════════════════════════════════════════

  function init() {
    if (!isDesktop()) return; // Mobile has its own overlay

    input   = document.querySelector('.ssda-search-input');
    overlay = document.getElementById('ssda-search-overlay');
    form    = document.querySelector('.ssda-search-form');

    if (!input || !overlay) return;

    resultsPane = overlay.querySelector('.srch-results');
    if (!resultsPane) return;

    // ── Input: focus → open + idle/restore ──────────────────────────────
    input.addEventListener('focus', function () {
      openOverlay();
      if (!input.value.trim()) {
        renderIdle();
      } else {
        query(input.value);
      }
    });

    // ── Input: typing → live query ───────────────────────────────────────
    input.addEventListener('input', function () {
      query(input.value);
    });

    // ── Keyboard navigation ──────────────────────────────────────────────
    input.addEventListener('keydown', function (e) {
      if (!isOpen) return;
      var items = getFocusable();

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActive(activeIdx + 1);
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (activeIdx <= 0) {
            activeIdx = -1;
            items.forEach(function (el) { el.classList.remove('srch-item-active'); });
            input.focus();
          } else {
            setActive(activeIdx - 1);
          }
          break;

        case 'Enter':
          if (activeIdx >= 0 && items[activeIdx]) {
            e.preventDefault();
            items[activeIdx].click();
          }
          // else let form submit naturally → /search?q=...
          break;

        case 'Escape':
          closeOverlay();
          input.blur();
          break;
      }
    });

    // Keyboard nav within overlay (Esc to return to input)
    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeOverlay();
        input.focus();
      }
    });

    // ── Click outside → close ────────────────────────────────────────────
    document.addEventListener('click', function (e) {
      if (!isOpen) return;
      if (
        overlay.contains(e.target) ||
        (form && form.contains(e.target))
      ) return;
      closeOverlay();
    }, true);

    // ── Close if viewport drops to mobile ────────────────────────────────
    window.addEventListener('resize', function () {
      if (!isDesktop() && isOpen) closeOverlay();
    }, { passive: true });

    // ── Expose close for NavSystem coordination ──────────────────────────
    window.SSearch = { close: closeOverlay, isOpen: function () { return isOpen; } };
  }

  // ════════════════════════════════════════════════════════════════════════
  // MOBILE INIT
  // Wires the mobi overlay search — same API + cache as desktop.
  // Renders a 2-column product grid into #mobi-suggest.
  // Runs unconditionally; uses MutationObserver to track panel open/close.
  // ════════════════════════════════════════════════════════════════════════

  function initMobile() {
    var mobiInput   = document.querySelector('[data-mobi-search]');
    var mobiSuggest = document.getElementById('mobi-suggest');
    var mobiPanel   = document.getElementById('mobi-panel');

    if (!mobiInput || !mobiSuggest) return;

    var mobiDebTimer = null;
    var mobiLastQ    = '';

    // ── Render helpers ─────────────────────────────────────────────────

    function mobiCard(p, q) {
      var imgSrc   = p.featured_image ? (p.featured_image.url || p.featured_image) : (p.image || '');
      var imgThumb = imgSrc ? imgSrc.replace(/(\.\w+)(\?|$)/, '_400x$1$2') : '';
      var price    = formatPrice(p.price || p.price_min || p.variants_min_price || '');
      return [
        '<a href="', esc(p.url), '" class="mobi-srch-product">',
          '<div class="mobi-srch-product__img-wrap">',
            imgThumb
              ? '<img class="mobi-srch-product__img" src="' + esc(imgThumb) + '" alt="" loading="lazy">'
              : '<div class="mobi-srch-product__img mobi-srch-product__img--placeholder"></div>',
          '</div>',
          '<p class="mobi-srch-product__title">', highlight(p.title, q), '</p>',
          price ? '<p class="mobi-srch-product__price">' + esc(price) + '</p>' : '',
        '</a>',
      ].join('');
    }

    function mobiSet(html) {
      mobiSuggest.innerHTML = html;
      mobiSuggest.removeAttribute('hidden');
    }

    function mobiHide() {
      mobiSuggest.setAttribute('hidden', '');
      mobiSuggest.innerHTML = '';
      mobiLastQ = '';
    }

    function mobiIdle() {
      var html = '<div class="mobi-srch-idle">';
      html += '<p class="mobi-srch-group__label">Explore</p>';
      html += '<div class="mobi-srch-cats">';
      html += EDITORIAL_LINKS.map(function (c) {
        return '<a href="' + esc(c.url) + '" class="mobi-srch-cat">' + esc(c.title) + '</a>';
      }).join('');
      html += '</div></div>';
      mobiSet(html);
    }

    function mobiResults(data, q) {
      var res      = (data.resources && data.resources.results) || {};
      var products = (res.products    || []).slice(0, CFG.PRODUCT_LIMIT);
      var colls    = (res.collections || []).slice(0, CFG.COLL_LIMIT);

      if (!products.length && !colls.length) { mobiEmpty(q); return; }

      var html = '';

      if (products.length) {
        html += '<div class="mobi-srch-products">';
        html += products.map(function (p) { return mobiCard(p, q); }).join('');
        html += '</div>';
      }

      if (colls.length) {
        html += '<div class="mobi-srch-group">';
        html += '<p class="mobi-srch-group__label">Collections</p>';
        html += '<div class="mobi-srch-cats">';
        html += colls.map(function (c) {
          return '<a href="' + esc(c.url) + '" class="mobi-srch-cat">' + highlight(c.title, q) + '</a>';
        }).join('');
        html += '</div></div>';
      }

      html += '<div class="mobi-srch-footer">';
      html += '<a href="/search?q=' + encodeURIComponent(q) + '&type=product" class="mobi-srch-viewall">';
      html += 'All results for &ldquo;' + esc(q) + '&rdquo;</a></div>';

      mobiSet(html);
    }

    function mobiEmpty(q) {
      var html = '<div class="mobi-srch-empty">';
      html += '<p class="mobi-srch-empty__msg">No results for &ldquo;' + esc(q) + '&rdquo;</p>';
      html += '<p class="mobi-srch-group__label">Explore</p>';
      html += '<div class="mobi-srch-cats">';
      html += EDITORIAL_LINKS.map(function (c) {
        return '<a href="' + esc(c.url) + '" class="mobi-srch-cat">' + esc(c.title) + '</a>';
      }).join('');
      html += '</div></div>';
      mobiSet(html);
    }

    // ── Input events ──────────────────────────────────────────────────

    mobiInput.addEventListener('input', function () {
      var q = mobiInput.value.trim();
      mobiLastQ = q;
      clearTimeout(mobiDebTimer);

      if (!q) { mobiIdle(); return; }

      mobiDebTimer = setTimeout(function () {
        fetchSuggestions(q).then(function (data) {
          if (mobiLastQ.trim() !== q) return; // stale guard
          mobiResults(data, q);
        }).catch(function () { mobiEmpty(q); });
      }, CFG.DEBOUNCE_MS);
    });

    mobiInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { mobiHide(); mobiInput.blur(); }
    });

    // ── Track panel open/close ────────────────────────────────────────

    if (mobiPanel) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName !== 'class') return;
          if (mobiPanel.classList.contains('is-open')) {
            // Panel just opened — show idle or current query results
            if (!mobiInput.value.trim()) { mobiIdle(); } else { mobiInput.dispatchEvent(new Event('input')); }
          } else {
            mobiHide();
            mobiInput.value = '';
          }
        });
      }).observe(mobiPanel, { attributes: true });
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); initMobile(); });
  } else {
    init();
    initMobile();
  }

})();
